import {
  Injectable,
  PLATFORM_ID,
  Signal,
  WritableSignal,
  effect,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import type { DashboardChartSourceKey } from '../types';
import { DateFilterService } from './date-filter.service';

const INITIAL_DELAY_MS = 1000;
const REFRESH_DELAY_MS = 800;

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dateFilter = inject(DateFilterService);
  private readonly loadingByKey = new Map<string, WritableSignal<boolean>>();
  private readonly timeouts = new Map<string, number>();

  constructor() {
    let firstRun = true;
    effect(() => {
      this.dateFilter.range();
      if (firstRun) {
        firstRun = false;
        return;
      }
      this.reset(REFRESH_DELAY_MS);
    });
  }

  metricLoading(key: string): Signal<boolean> {
    return this.ensureLoading(`metric:${key}`);
  }

  chartLoading(sourceKey: DashboardChartSourceKey): Signal<boolean> {
    return this.ensureLoading(`chart:${sourceKey}`);
  }

  reset(delayMs: number = INITIAL_DELAY_MS): void {
    for (const state of this.loadingByKey.values()) state.set(true);
    for (const key of this.loadingByKey.keys()) this.scheduleResolve(key, delayMs);
  }

  private ensureLoading(key: string): Signal<boolean> {
    const existing = this.loadingByKey.get(key);
    if (existing) return existing.asReadonly();
    const state = signal<boolean>(true);
    this.loadingByKey.set(key, state);
    this.scheduleResolve(key, INITIAL_DELAY_MS);
    return state.asReadonly();
  }

  private scheduleResolve(key: string, delayMs: number): void {
    const state = this.loadingByKey.get(key);
    if (!state) return;
    if (!isPlatformBrowser(this.platformId)) {
      state.set(false);
      return;
    }
    const existingTimeout = this.timeouts.get(key);
    if (existingTimeout !== undefined) window.clearTimeout(existingTimeout);
    const handle = window.setTimeout(() => {
      state.set(false);
      this.timeouts.delete(key);
    }, delayMs);
    this.timeouts.set(key, handle);
  }
}
